/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { useMemo } from "react";

import type { Primitive } from "../../state/use-url-state";
import type {
  FilterRecord,
  StandardFiltersConfig,
  UrlSyncState,
} from "./types";
import { sanitizeSegment } from "./sanitize-segment";

export function useStandardFilterUrlDerivations<
  TFilter extends FilterRecord,
>(config: StandardFiltersConfig<TFilter>, id: string) {
  const { initialFilters = {} } = config;

  const sanitizedInitialFilters = useMemo(() => {
    const {
      page: _ignoredPage,
      pageSize: _ignoredPageSize,
      search: _ignoredSearch,
      ...rest
    } = (initialFilters ?? {}) as Record<string, unknown>;
    return rest;
  }, [initialFilters]);

  const shouldSyncWithUrl = config.syncWithUrl === true;

  const resolvedUrlId = useMemo(() => {
    if (!shouldSyncWithUrl) {
      return undefined;
    }
    if (config.urlId) {
      return sanitizeSegment(config.urlId);
    }
    return `${sanitizeSegment(id)}-list`;
  }, [shouldSyncWithUrl, config.urlId, id]);

  const urlKeyMap = useMemo(() => {
    if (!shouldSyncWithUrl) {
      return {
        search: "search",
        page: "page",
        pageSize: "pageSize",
      };
    }

    const prefix = resolvedUrlId ?? "filters";
    return {
      search: config.urlKeys?.search ?? `${prefix}-search`,
      page: config.urlKeys?.page ?? `${prefix}-page`,
      pageSize: config.urlKeys?.pageSize ?? `${prefix}-limit`,
    };
  }, [
    shouldSyncWithUrl,
    resolvedUrlId,
    config.urlKeys?.search,
    config.urlKeys?.page,
    config.urlKeys?.pageSize,
  ]);

  const searchKey = urlKeyMap.search;
  const pageKey = urlKeyMap.page;
  const pageSizeKey = urlKeyMap.pageSize;

  const filterKeys = useMemo(
    () => Object.keys(sanitizedInitialFilters) as (keyof TFilter & string)[],
    [sanitizedInitialFilters],
  );

  const filterUrlKeyMap = useMemo(() => {
    const prefix = resolvedUrlId ?? sanitizeSegment(id);
    return filterKeys.reduce<Record<string, string>>((acc, key) => {
      const override =
        shouldSyncWithUrl && config.filterUrlKeys
          ? config.filterUrlKeys[key]
          : undefined;
      acc[key] =
        override ??
        (shouldSyncWithUrl
          ? `${prefix}-${sanitizeSegment(key)}`
          : sanitizeSegment(key));
      return acc;
    }, {});
  }, [
    filterKeys,
    config.filterUrlKeys,
    shouldSyncWithUrl,
    resolvedUrlId,
    id,
  ]);

  const urlKeysArray = useMemo<(keyof UrlSyncState)[]>(() => {
    if (!shouldSyncWithUrl) {
      return [] as (keyof UrlSyncState)[];
    }
    const keys: string[] = [searchKey, pageKey, pageSizeKey];
    for (const key of filterKeys) {
      const urlKey = filterUrlKeyMap[key];
      if (urlKey) {
        keys.push(urlKey);
      }
    }
    return keys as (keyof UrlSyncState)[];
  }, [
    shouldSyncWithUrl,
    searchKey,
    pageKey,
    pageSizeKey,
    filterKeys,
    filterUrlKeyMap,
  ]);

  const filterDefaults = useMemo<Partial<UrlSyncState>>(() => {
    if (!shouldSyncWithUrl) {
      return {};
    }
    const defaults: Partial<UrlSyncState> = {};
    for (const key of filterKeys) {
      const urlKey = filterUrlKeyMap[key];
      if (!urlKey) continue;
      const value = sanitizedInitialFilters[key];
      if (value !== undefined) {
        defaults[urlKey] = value as Primitive;
      }
    }
    return defaults;
  }, [shouldSyncWithUrl, filterKeys, filterUrlKeyMap, sanitizedInitialFilters]);

  return {
    shouldSyncWithUrl,
    sanitizedInitialFilters,
    searchKey,
    pageKey,
    pageSizeKey,
    filterKeys,
    filterUrlKeyMap,
    urlKeysArray,
    filterDefaults,
  };
}
